<template>
    <div class="stats-chart" ref="wrap"
        v-bind:class="{ 'stats-chart--fill': fill }"
        v-bind:style="wrapStyle"
        v-on:mouseleave="hideHover"
        v-on:touchend="hideHover"
        v-on:touchcancel="hideHover">

        <div v-if="loading" class="stats-chart__skeleton skeleton"></div>

        <div v-else-if="isEmpty" class="stats-chart__empty">
            <slot name="empty">{{ $t('common.no_data') }}</slot>
        </div>

        <svg v-else-if="width > 0 && innerHeight > 0" class="stats-chart__svg"
            v-bind:width="width"
            v-bind:height="innerHeight"
            v-bind:viewBox="`0 0 ${width} ${innerHeight}`"
            v-on:mousemove="onPointerMove"
            v-on:touchstart.passive="onPointerMove"
            v-on:touchmove.passive="onPointerMove">

            <defs>
                <linearGradient v-for="item in drawn" v-bind:key="`grad_${item.key}`"
                    v-bind:id="gradientId(item.key)" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" v-bind:style="{ stopColor: item.color, stopOpacity: .3 }"/>
                    <stop offset="1" v-bind:style="{ stopColor: item.color, stopOpacity: 0 }"/>
                </linearGradient>
                <clipPath v-bind:id="clipId">
                    <rect v-bind:x="plot.left" y="0" v-bind:width="plot.width + 1" v-bind:height="plot.bottom + 1"/>
                </clipPath>
            </defs>

            <!-- Horizontal grid with the value labels in the right gutter: -->
            <g v-if="!hideValues" class="stats-chart__grid">
                <line v-for="tick in yTicks" v-bind:key="`grid_${tick}`"
                    v-bind:x1="plot.left" v-bind:x2="plot.right"
                    v-bind:y1="yScale(tick)" v-bind:y2="yScale(tick)"/>
            </g>
            <template v-if="!hideValues">
                <text v-for="tick in yTickItems" v-bind:key="`ylabel_${tick.value}`"
                    class="stats-chart__label"
                    v-bind:x="plot.right + 8"
                    v-bind:y="yScale(tick.value) + 4">{{ tick.label }}</text>
            </template>

            <!-- Time axis: -->
            <text v-for="tick in xTickItems" v-bind:key="`xlabel_${tick.value}`"
                class="stats-chart__label"
                text-anchor="middle"
                v-bind:x="tick.x"
                v-bind:y="innerHeight - 5">{{ tick.label }}</text>

            <g v-bind:clip-path="`url(#${clipId})`">
                <!-- Secondary bars sit in a band at the bottom (volume, staked): -->
                <g v-for="item in drawnBars" v-bind:key="`bars_${item.key}`"
                    v-bind:class="['stats-chart__bars', { 'stats-chart__bars--secondary': item.axis === 'y2' }]">
                    <rect v-for="(point, index) in item.data" v-bind:key="`bar_${item.key}_${index}`"
                        v-bind:x="xScale(point.t) - item.barWidth / 2"
                        v-bind:y="barTop(item, point)"
                        v-bind:width="item.barWidth"
                        v-bind:height="barHeight(item, point)"
                        v-bind:rx="item.barWidth / 2"
                        v-bind:fill="item.color"
                        v-bind:opacity="item.axis === 'y2' ? .35 : (hover && hover.index !== index ? .55 : .95)"/>
                </g>

                <!-- Areas and lines: -->
                <g v-for="item in drawnLines" v-bind:key="`line_${item.key}`">
                    <path v-if="item.type === 'area'" class="stats-chart__area"
                        v-bind:d="item.areaPath"
                        v-bind:fill="`url(#${gradientId(item.key)})`"/>
                    <path class="stats-chart__line"
                        v-bind:d="item.linePath"
                        v-bind:stroke="item.color"/>

                    <template v-if="item.futurePath">
                        <path v-if="item.type === 'area'" class="stats-chart__area stats-chart__area--future"
                            v-bind:d="item.futureAreaPath"
                            v-bind:fill="`url(#${gradientId(item.key)})`"/>
                        <path class="stats-chart__line stats-chart__line--future"
                            v-bind:d="item.futurePath"
                            v-bind:stroke="item.color"/>
                    </template>
                </g>
            </g>

            <!-- A vertical marker, e.g. today on a schedule: -->
            <g v-if="markerX !== null" class="stats-chart__marker">
                <line v-bind:x1="markerX" v-bind:x2="markerX" v-bind:y1="plot.top - 4" v-bind:y2="plot.bottom"/>
                <text v-bind:x="markerX" v-bind:y="plot.top - 8" text-anchor="middle" class="stats-chart__label stats-chart__marker-label">{{ marker.label }}</text>
            </g>

            <!-- Crosshair: -->
            <g v-if="hover" class="stats-chart__crosshair">
                <line v-bind:x1="hover.x" v-bind:x2="hover.x" v-bind:y1="plot.top - 4" v-bind:y2="plot.bottom"/>
                <circle v-for="point in hoverDots" v-bind:key="`dot_${point.key}`"
                    v-bind:cx="hover.x" v-bind:cy="point.y" r="4"
                    v-bind:fill="point.color"/>
            </g>
        </svg>

        <div v-if="hover" class="stats-chart__tip" v-bind:style="tipStyle" ref="tip">
            <div class="stats-chart__tip-date">{{ hover.date }}</div>
            <div v-for="point in hover.points" v-bind:key="`tip_${point.key}`" class="stats-chart__tip-row">
                <i class="stats-chart__tip-dot" v-bind:style="{ background: point.color }"></i>
                <span class="stats-chart__tip-label">{{ point.label }}</span>
                <span class="stats-chart__tip-value">{{ point.text }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { scaleLinear, line as d3line, area as d3area, curveMonotoneX, curveBasis } from 'd3';
import { compactNumber, downsample, formatAxisDate, formatTooltipDate, DAY_MS } from './statsFormat.js';

const HOUR_MS = 60 * 60 * 1000;

/**
 * Picks the time-axis ticks for the span: hours for a day, days for a month,
 * months for a year, years beyond. Ticks sit on natural boundaries (midnight,
 * the first of the month) instead of an even split of the range, which is
 * what makes the axis readable.
 */
const timeTicks = function timeTicks(x0, x1, plotWidth, locale) {
    const span = x1 - x0;
    const maxLabels = Math.max(2, Math.floor(plotWidth / 64));
    const ticks = [];

    if (span <= 3 * DAY_MS) {
        const step = [1, 2, 3, 4, 6, 12, 24].find(hours => span / (hours * HOUR_MS) <= maxLabels) || 24;
        const start = new Date(x0);
        start.setMinutes(0, 0, 0);
        while (start.getHours() % step !== 0 || start.valueOf() < x0) {
            start.setHours(start.getHours() + 1);
        }
        for (let t = start; t.valueOf() <= x1; t.setHours(t.getHours() + step)) {
            const isMidnight = t.getHours() === 0 && span > DAY_MS;
            ticks.push({ value: t.valueOf(), label: formatAxisDate(t.valueOf(), isMidnight ? 'day' : 'hour', locale) });
        }
        return ticks;
    }

    if (span <= 130 * DAY_MS) {
        const step = [1, 2, 3, 5, 7, 14, 30].find(days => span / (days * DAY_MS) <= maxLabels) || 30;
        const start = new Date(x0);
        start.setHours(0, 0, 0, 0);
        if (start.valueOf() < x0) {
            start.setDate(start.getDate() + 1);
        }
        for (let t = start; t.valueOf() <= x1; t.setDate(t.getDate() + step)) {
            ticks.push({ value: t.valueOf(), label: formatAxisDate(t.valueOf(), 'day', locale) });
        }
        return ticks;
    }

    if (span <= 4 * 365 * DAY_MS) {
        const months = span / (30.4 * DAY_MS);
        const step = [1, 2, 3, 4, 6, 12].find(m => months / m <= maxLabels) || 12;
        const start = new Date(x0);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        if (start.valueOf() < x0) {
            start.setMonth(start.getMonth() + 1);
        }
        while (start.getMonth() % step !== 0) {
            start.setMonth(start.getMonth() + 1);
        }
        for (let t = start; t.valueOf() <= x1; t.setMonth(t.getMonth() + step)) {
            ticks.push({ value: t.valueOf(), label: formatAxisDate(t.valueOf(), 'month', locale) });
        }
        return ticks;
    }

    const start = new Date(x0);
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
    if (start.valueOf() < x0) {
        start.setFullYear(start.getFullYear() + 1);
    }
    const yearStep = Math.max(1, Math.ceil((span / (365 * DAY_MS)) / maxLabels));
    for (let t = start; t.valueOf() <= x1; t.setFullYear(t.getFullYear() + yearStep)) {
        ticks.push({ value: t.valueOf(), label: formatAxisDate(t.valueOf(), 'year', locale) });
    }
    return ticks;
};

// A bar needs this many pixels of the axis (bar plus gap) to stay apart
// from its neighbours; denser series are averaged into fewer bars.
const BAR_SLOT_PX = 6;

/**
 * How many points go into one bar so that at most `count` bars are drawn:
 * whole buckets of one size, so the bars stay evenly spaced.
 */
const bucketSize = (length, count) => (length <= count ? 1 : Math.ceil(length / count));

/**
 * Bars thinner than a few pixels blur into a comb, so a dense series is
 * averaged into at most `count` buckets before drawing. Buckets are counted
 * from the newest point, so a short one (if any) lands at the old edge; it keeps
 * the slot of a full bucket, otherwise it would stand closer to its neighbour.
 */
const bucketBars = function bucketBars(points, count) {
    const size = bucketSize(points.length, count);
    if (size === 1) {
        return points;
    }
    const step = (points.at(-1).t - points[0].t) / (points.length - 1);
    const result = [];
    for (let end = points.length; end > 0; end -= size) {
        const slice = points.slice(Math.max(0, end - size), end);
        result.unshift({
            t: slice.length === size
                ? slice.reduce((acc, p) => acc + p.t, 0) / size
                : slice.at(-1).t - ((size - 1) * step) / 2,
            v: slice.reduce((acc, p) => acc + p.v, 0) / slice.length,
        });
    }
    return result;
};

const bisect = function bisectNearest(points, t) {
    let lo = 0;
    let hi = points.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1; // eslint-disable-line no-bitwise
        if (points[mid].t < t) {
            lo = mid + 1;
        } else {
            hi = mid;
        }
    }
    if (lo > 0 && Math.abs(points[lo - 1].t - t) < Math.abs(points[lo].t - t)) {
        return lo - 1;
    }
    return lo;
};

/**
 * SVG time-series chart: areas, lines and bars over a shared time axis, a grid
 * with value labels in the right gutter, a crosshair with a tooltip and an
 * optional vertical marker. Series: [{ key, label, color, type, axis, data, format, splitAt }].
 * Everything is drawn from computed props, so a theme or locale switch just re-renders.
 */
export default {
    props: {
        series: {
            type: Array,
            default: () => [],
        },
        height: {
            type: Number,
            default: 220,
        },
        // Take the height of the parent instead of the fixed one, so the chart
        // can fill whatever room a card has left under its hero:
        fill: Boolean,
        baseline: {
            type: String,
            default: 'auto', // 'auto' zooms to the data, 'zero' starts at zero
        },
        yFormat: Function,
        marker: Object, // { t, label }
        withTime: {
            type: Boolean,
            default: undefined,
        },
        loading: Boolean,
        // A B-spline through the points instead of the exact monotone curve:
        // rounder, only shows the trend, for the small teaser charts.
        smooth: Boolean,
        // No value labels on the right and no grid lines, the plot takes the
        // whole width: for the teaser charts that only show the trend.
        hideValues: Boolean,
        maxPoints: {
            type: Number,
            default: 420,
        },
    },

    data() {
        return {
            width: 0,
            measuredHeight: 0,
            hover: null,
            tipWidth: 180,
            resizeObserver: undefined,
        };
    },

    computed: {
        locale() {
            return this.$i18n.locale;
        },

        clipId() {
            return `stats-chart-clip-${this._uid}`; // eslint-disable-line no-underscore-dangle
        },

        innerHeight() {
            return this.fill ? this.measuredHeight : this.height;
        },

        wrapStyle() {
            return this.fill ? undefined : { height: `${this.height}px` };
        },

        primary() {
            return this.series.filter(item => item.axis !== 'y2' && item.data?.length);
        },

        secondary() {
            return this.series.filter(item => item.axis === 'y2' && item.data?.length);
        },

        isEmpty() {
            return this.primary.length === 0;
        },

        xDomain() {
            let min = Infinity;
            let max = -Infinity;
            // Bars count by their drawn centres, which may lie outside the raw points:
            const domainData = item => (item.type === 'bar' && item.data?.length
                ? bucketBars(item.data, this.barBuckets)
                : item.data || []);
            this.series.forEach((item) => {
                domainData(item).forEach((point) => {
                    if (point.t < min) min = point.t;
                    if (point.t > max) max = point.t;
                });
            });
            if (this.marker && Number.isFinite(this.marker.t)) {
                min = Math.min(min, this.marker.t);
                max = Math.max(max, this.marker.t);
            }

            // Bars are centred on their timestamps, so the axis gets half a step
            // of room at both ends; without it the first bar is cut in half by
            // the plot edge and the last one sticks out into the label gutter.
            // The step is the one of the drawn bars, which may cover several points each.
            const barStep = this.series
                .filter(item => item.type === 'bar' && item.data?.length > 1)
                .reduce((acc, item) => {
                    const step = (item.data.at(-1).t - item.data[0].t) / (item.data.length - 1);
                    return Math.min(acc, step * bucketSize(item.data.length, this.barBuckets));
                }, Infinity);
            if (Number.isFinite(barStep) && barStep > 0) {
                min -= barStep / 2;
                max += barStep / 2;
            }

            return max > min ? [min, max] : [min - 1, max + 1];
        },

        yDomain() {
            let min = Infinity;
            let max = -Infinity;
            this.primary.forEach((item) => {
                item.data.forEach((point) => {
                    if (point.v < min) min = point.v;
                    if (point.v > max) max = point.v;
                });
            });

            if (!Number.isFinite(min)) {
                return [0, 1];
            }

            const hasBars = this.primary.some(item => item.type === 'bar');

            // Bars stand on zero; lines zoom to the data so a year of price still has relief:
            if (this.baseline === 'zero' || hasBars || min <= 0) {
                return [Math.min(0, min), max === 0 ? 1 : max * 1.04];
            }

            const pad = Math.max((max - min) * 0.12, max * 1e-6);
            return [Math.max(0, min - pad), max + pad];
        },

        // The domain is not "niced": rounding it to whole ticks would pull a
        // $1.2–$3.5 year of price back down to $0. The grid lines sit on round
        // values inside the range instead, the way finance charts do it.
        yScaleBase() {
            return scaleLinear().domain(this.yDomain);
        },

        yTicks() {
            return this.yScaleBase.ticks(5);
        },

        /**
         * Tick labels get as many digits as it takes to tell neighbours apart:
         * a month of supply moves in the third decimal of "5.24 млрд".
         */
        yLabels() {
            let labels = [];
            for (let digits = 2; digits <= 6; digits += 1) {
                labels = this.yTicks.map(tick => this.formatY(tick, digits));
                if (new Set(labels).size === labels.length) {
                    break;
                }
            }
            return labels;
        },

        yTickItems() {
            return this.yTicks.map((value, index) => ({ value, label: this.yLabels[index] }));
        },

        plot() {
            const longest = this.yLabels.reduce((acc, label) => Math.max(acc, label.length), 3);
            const gutter = this.hideValues ? 2 : Math.round(longest * 6.4 + 12);
            const right = Math.max(40, this.width - gutter);
            const top = this.marker ? 22 : 12;
            const bottom = this.innerHeight - 22;
            return {
                left: 2,
                right,
                top,
                bottom,
                width: right - 2,
                height: bottom - top,
            };
        },

        xTicks() {
            return timeTicks(this.xDomain[0], this.xDomain[1], this.plot.width, this.locale);
        },

        // A label centred on the first or last tick would hang out of the svg
        // and, on a phone, touch the screen edge: the labels are kept inside.
        xTickItems() {
            return this.xTicks.map((tick) => {
                const half = tick.label.length * 3.2;
                const x = Math.max(half, Math.min(this.width - half, this.xScale(tick.value)));
                return { ...tick, x };
            });
        },

        // The most bars that fit the plot with a gap between them:
        barBuckets() {
            return Math.max(30, Math.floor(this.plot.width / BAR_SLOT_PX));
        },

        /**
         * Series prepared for drawing: downsampled, with the paths computed.
         */
        drawn() {
            const items = [];

            const y2Max = this.secondary.reduce((acc, item) => item.data.reduce((m, p) => Math.max(m, p.v), acc), 0) || 1;
            const y2Band = this.plot.height * 0.28;

            this.series.forEach((item) => {
                if (!item.data?.length) return;

                const isSecondary = item.axis === 'y2';
                const data = item.type === 'bar'
                    ? bucketBars(item.data, this.barBuckets)
                    : downsample(item.data, this.maxPoints);
                const scaleY = isSecondary
                    ? v => this.plot.bottom - (v / y2Max) * y2Band
                    : v => this.yScale(v);

                const entry = {
                    key: item.key,
                    label: item.label,
                    color: item.color,
                    type: item.type || 'area',
                    axis: isSecondary ? 'y2' : 'y',
                    format: item.format,
                    data,
                    scaleY,
                };

                if (entry.type === 'bar') {
                    const slot = data.length > 1 ? this.plot.width / data.length : this.plot.width / 2;
                    entry.barWidth = Math.max(1, Math.min(slot * 0.62, 24));
                    entry.baseY = isSecondary ? this.plot.bottom : this.yScale(Math.max(0, this.yDomain[0]));
                } else {
                    const curve = this.smooth ? curveBasis : curveMonotoneX;
                    const lineGen = d3line()
                        .x(p => this.xScale(p.t))
                        .y(p => scaleY(p.v))
                        .curve(curve);
                    const areaGen = d3area()
                        .x(p => this.xScale(p.t))
                        .y0(this.plot.bottom)
                        .y1(p => scaleY(p.v))
                        .curve(curve);

                    // The solid part runs exactly up to the split (today) and the
                    // dashed one starts there: a point is interpolated on the split
                    // instead of letting the dashed segment begin at the last point
                    // before it and leave a hole in front of the marker.
                    let past = data;
                    let future = [];
                    if (Number.isFinite(item.splitAt)) {
                        const idx = data.findIndex(p => p.t > item.splitAt);
                        if (idx > 0) {
                            const prev = data[idx - 1];
                            const next = data[idx];
                            const ratio = (item.splitAt - prev.t) / (next.t - prev.t);
                            const split = { t: item.splitAt, v: prev.v + (next.v - prev.v) * ratio };
                            past = [...data.slice(0, idx), split];
                            future = [split, ...data.slice(idx)];
                        } else if (idx === 0) {
                            past = [];
                            future = data;
                        }
                    }

                    entry.linePath = past.length ? lineGen(past) : '';
                    entry.areaPath = past.length ? areaGen(past) : '';
                    entry.futurePath = future.length ? lineGen(future) : '';
                    entry.futureAreaPath = future.length ? areaGen(future) : '';
                }

                items.push(entry);
            });

            // Bars go under the lines:
            return items.sort((a, b) => (a.type === 'bar' ? 0 : 1) - (b.type === 'bar' ? 0 : 1));
        },

        drawnBars() {
            return this.drawn.filter(item => item.type === 'bar');
        },

        drawnLines() {
            return this.drawn.filter(item => item.type !== 'bar');
        },

        hoverDots() {
            return this.hover ? this.hover.points.filter(point => point.type !== 'bar') : [];
        },

        markerX() {
            if (!this.marker || !Number.isFinite(this.marker.t)) {
                return null;
            }
            return this.xScale(this.marker.t);
        },

        showTime() {
            if (this.withTime !== undefined) {
                return this.withTime;
            }
            const span = this.xDomain[1] - this.xDomain[0];
            const lead = this.primary[0];
            // Intraday points get the clock even on a month-long range:
            const step = lead && lead.data.length > 1 ? span / lead.data.length : span;
            return span <= 10 * DAY_MS || step < 12 * 60 * 60 * 1000;
        },

        tipStyle() {
            if (!this.hover) {
                return {};
            }
            const gap = 14;
            let left = this.hover.x + gap;
            if (left + this.tipWidth > this.width - 4) {
                left = this.hover.x - this.tipWidth - gap;
            }
            if (left < 0) {
                left = 4;
            }
            return { left: `${left}px`, top: `${Math.max(0, this.plot.top - 8)}px` };
        },
    },

    watch: {
        series() {
            this.hover = null;
        },
    },

    mounted() {
        this.measure();

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.measure());
            this.resizeObserver.observe(this.$refs.wrap);
        } else {
            window.addEventListener('resize', this.measure);
        }
    },

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        } else {
            window.removeEventListener('resize', this.measure);
        }
    },

    methods: {
        measure() {
            const wrap = this.$refs.wrap;
            if (!wrap) return;
            const rect = wrap.getBoundingClientRect();
            const width = Math.round(rect.width);
            const height = Math.round(rect.height);
            if (width !== this.width) {
                this.width = width;
                this.hover = null;
            }
            if (this.fill && height !== this.measuredHeight) {
                this.measuredHeight = height;
                this.hover = null;
            }
        },

        gradientId(key) {
            return `stats-chart-grad-${this._uid}-${key}`; // eslint-disable-line no-underscore-dangle
        },

        xScale(t) {
            const [x0, x1] = this.xDomain;
            return this.plot.left + ((t - x0) / (x1 - x0)) * this.plot.width;
        },

        yScale(v) {
            const [y0, y1] = this.yScaleBase.domain();
            const ratio = y1 === y0 ? 0.5 : (v - y0) / (y1 - y0);
            return this.plot.bottom - ratio * this.plot.height;
        },

        formatY(value, digits = 2) {
            return this.yFormat
                ? this.yFormat(value, this.locale, digits)
                : compactNumber(value, this.locale, digits);
        },

        barTop(item, point) {
            return Math.min(item.scaleY(point.v), item.baseY);
        },

        barHeight(item, point) {
            return Math.max(1, Math.abs(item.baseY - item.scaleY(point.v)));
        },

        onPointerMove(event) {
            if (!this.drawn.length) return;

            const svg = this.$el.querySelector('svg');
            if (!svg) return;

            const rect = svg.getBoundingClientRect();
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            const px = clientX - rect.left;

            if (px < this.plot.left - 8 || px > this.plot.right + 8) {
                this.hover = null;
                return;
            }

            const [x0, x1] = this.xDomain;
            const t = x0 + ((Math.min(Math.max(px, this.plot.left), this.plot.right) - this.plot.left) / this.plot.width) * (x1 - x0);

            const lead = this.drawn.find(item => item.axis === 'y') || this.drawn[0];
            const leadIndex = bisect(lead.data, t);
            const leadPoint = lead.data[leadIndex];

            const points = this.drawn.map((item) => {
                const index = item === lead ? leadIndex : bisect(item.data, leadPoint.t);
                const point = item.data[index];
                const stale = Math.abs(point.t - leadPoint.t) > (this.xDomain[1] - this.xDomain[0]) / 40;
                const format = item.format || (value => compactNumber(value, this.locale));
                return {
                    key: item.key,
                    label: item.label,
                    color: item.color,
                    type: item.type,
                    y: item.scaleY(point.v),
                    text: stale ? '—' : format(point.v, this.locale),
                };
            });

            this.hover = {
                index: leadIndex,
                x: this.xScale(leadPoint.t),
                date: formatTooltipDate(leadPoint.t, this.showTime, this.locale),
                // The main series reads first in the tooltip, bars after it:
                points: points.sort((a, b) => (a.type === 'bar' ? 1 : 0) - (b.type === 'bar' ? 1 : 0)),
            };

            this.$nextTick(() => {
                if (this.$refs.tip) {
                    this.tipWidth = this.$refs.tip.offsetWidth;
                }
            });
        },

        hideHover() {
            this.hover = null;
        },
    },
};
</script>

<style lang="scss">
.stats-chart {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;

    // Fills the parent (a flex column item); the parent decides the height:
    &--fill {
        flex: 1 1 auto;
        min-height: 180px;
    }

    &__svg {
        display: block;
        overflow: visible;
        font-family: inherit;
    }

    &__skeleton {
        width: 100%;
        height: 100%;
        border-radius: 12px;
    }

    &__empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--body-muted-text-color);
        font-size: 13px;
    }

    &__grid line {
        stroke: var(--card-row-separator);
        stroke-width: 1;
        shape-rendering: crispEdges;
    }

    &__label {
        fill: var(--body-muted-text-color);
        font-size: 11px;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.01em;
    }

    &__line {
        fill: none;
        stroke-width: 2;
        stroke-linejoin: round;
        stroke-linecap: round;

        &--future {
            stroke-dasharray: 4 5;
            stroke-width: 1.5;
            opacity: .8;
        }
    }

    &__area--future {
        opacity: .45;
    }

    &__bars rect {
        transition: opacity .15s ease;
    }

    &__marker {
        line {
            stroke: var(--body-muted-text-color);
            stroke-width: 1;
            stroke-dasharray: 3 4;
            opacity: .7;
        }
    }

    &__marker-label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
    }

    &__crosshair {
        pointer-events: none;

        line {
            stroke: var(--body-muted-text-color);
            stroke-width: 1;
            stroke-dasharray: 2 4;
            opacity: .8;
        }

        circle {
            stroke: var(--card-background);
            stroke-width: 2;
        }
    }

    &__tip {
        position: absolute;
        z-index: 3;
        pointer-events: none;
        padding: 8px 11px;
        border-radius: 10px;
        background: var(--chart-tooltip-background);
        border: 1px solid var(--card-border-color);
        box-shadow: 0 8px 24px var(--card-box-shadow-color);
        font-size: 12px;
        color: var(--body-text-color);
        white-space: nowrap;
        line-height: 1.5;
    }

    &__tip-date {
        color: var(--body-muted-text-color);
        margin-bottom: 3px;
    }

    &__tip-row {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__tip-dot {
        display: inline-block;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    &__tip-label {
        color: var(--body-muted-text-color);
    }

    &__tip-value {
        margin-left: auto;
        padding-left: 12px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }
}
</style>
